// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AssetSortProperty,
  assetSortPropertySchema,
} from '@/asset/api/get-assets';
import { AssetAssignmentFilterMenu } from '@/asset/components/asset-assignment-filter-menu';
import { AssetFunctionalStatusFilterMenu } from '@/asset/components/asset-functional-status-filter-menu';
import { AssetImportModal } from '@/asset/components/asset-import-modal';
import { AssetLocationFilterMenu } from '@/asset/components/asset-location-filter-menu';
import { AssetMultiMoveDialog } from '@/asset/components/asset-multi-move-dialog';
import { AssetOwnershipFilterMenu } from '@/asset/components/asset-ownership-filter-menu';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import {
  OwnershipValue,
  assetSearchListingSchema,
  filterSchema,
  useAssetFilters,
} from '@/asset/hooks/use-asset-filters';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { PageContent } from '@/components/page-content';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import { AssetAssignmentStatus } from '@/constants/asset-assignment-status';
import {
  ASSET_FUNCTIONAL_STATUSES,
  AssetFunctionalStatus,
} from '@/constants/asset-functional-status';
import {
  AddRounded,
  ClearRounded,
  ForkRightOutlined,
  InventoryOutlined,
  KeyboardTab,
  ListRounded,
  PlaceOutlined,
  SaveOutlined,
  SettingsRounded,
  StartOutlined,
} from '@/constants/hardfin-standard-icons';
import { LocationCompanyType } from '@/constants/location-company-type';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { ItemFilterMenu } from '@/items/components/item-filter-menu';
import {
  Badge,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { omit, without } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { z } from 'zod';

const LOCAL_STORAGE_SORT_KEY = 'asset-sort';
const LOCAL_STORAGE_FILTERS_KEY = 'asset-filters';
const DEFAULT_SORT_PROPERTY: AssetSortProperty = 'serial';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

const sortSchema = z.object({
  property: assetSortPropertySchema,
  direction: sortDirectionSchema,
});

// Type for the asset location toggle filter values.
// These map to either inventoryStatus or transitStatus filters.
type AssetLocationFilterValue =
  | 'all'
  | 'in_stock' // InventoryStatus: in_inventory
  | 'in_transit_to_field' // TransitStatus: in_transit_to_field
  | 'in_the_field' // InventoryStatus: not_in_inventory
  | 'returning_to_stock'; // TransitStatus: in_transit_to_inventory

// Options for the asset location toggle button group
const ASSET_LOCATION_FILTER_OPTIONS: FilterOption<AssetLocationFilterValue>[] =
  [
    {
      value: 'all',
      label: 'All',
      icon: <ListRounded fontSize="small" />,
    },
    {
      value: 'in_stock',
      label: 'In stock',
      icon: <InventoryOutlined fontSize="small" />,
    },
    {
      value: 'in_transit_to_field',
      label: 'In transit to field',
      icon: <StartOutlined fontSize="small" />,
    },
    {
      value: 'in_the_field',
      label: 'In the field',
      icon: <PlaceOutlined fontSize="small" />,
    },
    {
      value: 'returning_to_stock',
      label: 'Returning to stock',
      icon: <KeyboardTab fontSize="small" />,
    },
  ];

export const Route = createFileRoute('/_app/assets/')({
  component: AssetsRoute,
  validateSearch: assetSearchListingSchema,
});

/**
 * Assets page component that displays a filterable, sortable, and paginated table of assets.
 *
 * Important: When any filter changes, the page parameter is omitted to reset pagination
 * to page 1. This ensures users see relevant results after filtering since the filtered
 * dataset may have fewer pages than the current page number.
 */
function AssetsRoute() {
  const navigate = Route.useNavigate();
  const searchParameters = Route.useSearch();
  const location = useLocation();

  // A stale or hand-edited currency parses to undefined at the route boundary. Drop it from
  // the URL too, so the address bar stops advertising a filter that is not applied and the
  // backend -- which rejects an unknown code outright -- never receives it.
  const rawCurrency = new URLSearchParams(location.searchStr).get('currency');
  const hasStaleCurrency =
    rawCurrency !== null && searchParameters.currency === undefined;
  useEffect(() => {
    if (!hasStaleCurrency) return;
    navigate({
      search: { ...searchParameters, currency: undefined },
      replace: true,
    });
  }, [hasStaleCurrency, navigate, searchParameters]);

  const menu = useDisclosure();
  const assetImportModal = useDisclosure();

  const multiMoveDialog = useDisclosure(searchParameters.move ?? false);

  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const searchContainer = useRef<HTMLInputElement>(null);

  // Track whether initial page load has completed. Default filters should only
  // be applied on initial load, not when the user actively clears all filters.
  const hasInitialized = useRef(false);

  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  const [defaultFilters, setDefaultFilters] = useLocalStorage(
    LOCAL_STORAGE_FILTERS_KEY,
    filterSchema
  );

  // On initial page load, sync default filters to URL so filter handlers work correctly.
  // This runs once on mount and updates the URL to include any saved default filters.
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Check if there are any filter params already in the URL
    const hasExistingFilters =
      searchParameters.products !== undefined ||
      searchParameters.locations !== undefined ||
      searchParameters.customers !== undefined ||
      searchParameters.ownership !== undefined ||
      searchParameters.assignment !== undefined ||
      searchParameters.transitStatus !== undefined ||
      searchParameters.inventoryStatus !== undefined ||
      searchParameters.customerLocations !== undefined ||
      searchParameters.functionalStatuses !== undefined ||
      searchParameters.locationCompanyTypes !== undefined ||
      searchParameters.scrapped !== undefined ||
      searchParameters.currency !== undefined;

    // If there are already filters in the URL or no defaults, skip syncing
    if (hasExistingFilters || !defaultFilters) return;

    // Check if there are any non-empty defaults to sync
    // Note: transitStatus and inventoryStatus are not saved as defaults
    const hasDefaultsToSync =
      (defaultFilters.productIds && defaultFilters.productIds.length > 0) ||
      (defaultFilters.locationIds && defaultFilters.locationIds.length > 0) ||
      (defaultFilters.customerIds && defaultFilters.customerIds.length > 0) ||
      (defaultFilters.ownership && defaultFilters.ownership.length > 0) ||
      (defaultFilters.assignment && defaultFilters.assignment.length > 0) ||
      (defaultFilters.customerLocations &&
        defaultFilters.customerLocations.length > 0) ||
      (defaultFilters.functionalStatuses &&
        defaultFilters.functionalStatuses.length > 0) ||
      (defaultFilters.locationCompanyTypes &&
        defaultFilters.locationCompanyTypes.length > 0) ||
      defaultFilters.scrapped === true;

    if (!hasDefaultsToSync) return;

    // Build URL params from defaults (note: schema uses different names than URL)
    const urlParams: Record<string, unknown> = { ...searchParameters };
    if (defaultFilters.productIds?.length)
      urlParams.products = defaultFilters.productIds;
    if (defaultFilters.locationIds?.length)
      urlParams.locations = defaultFilters.locationIds;
    if (defaultFilters.customerIds?.length)
      urlParams.customers = defaultFilters.customerIds;
    if (defaultFilters.ownership?.length)
      urlParams.ownership = defaultFilters.ownership;
    if (defaultFilters.assignment?.length)
      urlParams.assignment = defaultFilters.assignment;
    if (defaultFilters.customerLocations?.length)
      urlParams.customerLocations = defaultFilters.customerLocations;
    if (defaultFilters.functionalStatuses?.length)
      urlParams.functionalStatuses = defaultFilters.functionalStatuses;
    if (defaultFilters.locationCompanyTypes?.length)
      urlParams.locationCompanyTypes = defaultFilters.locationCompanyTypes;
    if (defaultFilters.scrapped) urlParams.scrapped = defaultFilters.scrapped;

    navigate({ search: urlParams, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length == 0) {
        // Reset to page 1 when search query changes since results may be different
        navigate({ search: { ...omit(searchParameters, 'page', 'query') } });
        return;
      }
      // Reset to page 1 when search query changes since results may be different
      navigate({ search: { ...omit(searchParameters, 'page'), query } });
    }, 300);
  }, [navigate, searchParameters]);

  // The current sort state is constructed by first checking the URL search
  // parameters. If the search parameters are not present, the sort preference
  // from local storage is used. If the sort preference is not present, the
  // default sort property and direction are used.
  const sort = {
    property:
      searchParameters.sort ??
      sortPreference?.property ??
      DEFAULT_SORT_PROPERTY,
    direction:
      searchParameters.direction ??
      sortPreference?.direction ??
      DEFAULT_SORT_DIRECTION,
  };

  // Pass null for defaults since we sync them to URL on mount via the useEffect above.
  // This ensures all filter state comes from URL params, making filter handlers work correctly.
  const { apiFilter: assetFilter, uiFilters } = useAssetFilters(
    searchParameters,
    null,
    sort
  );

  const functionalStatusOptions: AssetFunctionalStatus[] = uiFilters.scrapped
    ? ['FUNCTIONAL', 'NEEDS_REVIEW', 'NON_FUNCTIONAL', 'SCRAPPED']
    : ASSET_FUNCTIONAL_STATUSES;

  const setSort = (property: AssetSortProperty, direction: SortDirection) => {
    setSortPreference({ property, direction });
    navigate({ search: { ...searchParameters, direction, sort: property } });
  };

  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  const handleChangeLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    const searchParams = { ...searchParameters };

    if (locationIds.length === 0) {
      delete searchParams.locations;
    } else {
      searchParams.locations = locationIds;
    }

    if (customerIds.length === 0) {
      delete searchParams.customerLocations;
    } else {
      searchParams.customerLocations = customerIds;
    }

    if (companyTypes.length === 0) {
      delete searchParams.locationCompanyTypes;
    } else {
      searchParams.locationCompanyTypes = companyTypes;
    }

    navigate({ search: searchParams });
  };

  const handleChangeProducts = (products: string[]) => {
    // Reset to page 1 when filters change since results may be different
    const searchParams = omit(searchParameters, 'page');

    if (products.length === 0) {
      delete searchParams.products;
    } else {
      searchParams.products = products;
    }

    navigate({ search: searchParams });
  };

  const handleChangeOwnership = (
    ownership: OwnershipValue[],
    customers: string[]
  ) => {
    // Reset to page 1 when filters change since results may be different
    const searchParams = omit(searchParameters, 'page');

    if (ownership.length === 0) {
      delete searchParams.ownership;
    } else {
      searchParams.ownership = ownership;
    }

    if (customers.length === 0) {
      delete searchParams.customers;
    } else {
      searchParams.customers = customers;
    }

    navigate({ search: searchParams });
  };

  const handleChangeFunctionalStatuses = (
    functionalStatuses: AssetFunctionalStatus[]
  ) => {
    // Reset to page 1 when filters change since results may be different
    const searchParams = omit(searchParameters, 'page');

    if (functionalStatuses.length === 0) {
      delete searchParams.functionalStatuses;
    } else {
      searchParams.functionalStatuses = functionalStatuses;
    }

    navigate({
      search: searchParams,
    });
  };

  const handleChangePage = (pageNumber: number) => {
    navigate({ search: { ...searchParameters, page: pageNumber } });
  };

  /**
   * Compute the current asset location filter value for the toggle.
   * Maps from inventoryStatus/transitStatus filter values back to toggle values.
   */
  const computeCurrentLocationFilter = (): AssetLocationFilterValue => {
    // Check transit status first (more specific)
    if (uiFilters.transitStatus.length === 1) {
      if (uiFilters.transitStatus[0] === 'in_transit_to_field') {
        return 'in_transit_to_field';
      }
      if (uiFilters.transitStatus[0] === 'in_transit_to_inventory') {
        return 'returning_to_stock';
      }
    }
    // Check inventory status
    if (uiFilters.inventoryStatus.length === 1) {
      if (uiFilters.inventoryStatus[0] === 'in_inventory') {
        return 'in_stock';
      }
      if (uiFilters.inventoryStatus[0] === 'not_in_inventory') {
        return 'in_the_field';
      }
    }
    return 'all';
  };

  const currentLocationFilter = computeCurrentLocationFilter();

  /**
   * Handle asset location toggle change.
   * Sets either inventoryStatus or transitStatus filter based on the selected value.
   */
  const handleLocationToggleChange = (newValue: AssetLocationFilterValue) => {
    // Start with current params, clear page and both location-related filters
    const {
      page: _page,
      inventoryStatus: _inv,
      transitStatus: _trans,
      ...rest
    } = searchParameters;

    // Build new search params based on the selected value
    switch (newValue) {
      case 'in_stock':
        navigate({ search: { ...rest, inventoryStatus: ['in_inventory'] } });
        break;
      case 'in_transit_to_field':
        navigate({
          search: { ...rest, transitStatus: ['in_transit_to_field'] },
        });
        break;
      case 'in_the_field':
        navigate({
          search: { ...rest, inventoryStatus: ['not_in_inventory'] },
        });
        break;
      case 'returning_to_stock':
        navigate({
          search: { ...rest, transitStatus: ['in_transit_to_inventory'] },
        });
        break;
      case 'all':
      default:
        // Clear both filters
        navigate({ search: rest });
        break;
    }
  };

  const handleChangeAssignment = (assignment: AssetAssignmentStatus[]) => {
    // Reset to page 1 when filters change since results may be different
    const searchParams = omit(searchParameters, 'page');

    if (assignment.length === 0) {
      delete searchParams.assignment;
    } else {
      searchParams.assignment = assignment;
    }

    navigate({ search: searchParams });
  };

  const handleSort = (property: AssetSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const handleChangeScrapped = () => {
    if (!uiFilters.scrapped) {
      // Reset to page 1 when filters change since results may be different
      return navigate({
        search: { ...omit(searchParameters, 'page'), scrapped: true },
      });
    }

    const updatedFunctionalStatuses = without(
      uiFilters.functionalStatuses,
      'SCRAPPED'
    );
    // Reset to page 1 when filters change since results may be different
    const searchParams = omit(searchParameters, ['scrapped', 'page']);

    if (updatedFunctionalStatuses.length === 0) {
      delete searchParams.functionalStatuses;
    } else {
      searchParams.functionalStatuses = updatedFunctionalStatuses;
    }

    return navigate({
      search: searchParams,
    });
  };

  /**
   * Saves the current filter settings as default filters and closes the menu.
   * Note: inventoryStatus and transitStatus are excluded as they are controlled
   * by the toggle buttons and should not be saved as default filters.
   */
  const handleSaveDefaultFilters = () => {
    const currentFilters = {
      ownership: uiFilters.ownership,
      assignment: uiFilters.assignment,
      productIds: uiFilters.productIds,
      locationIds: uiFilters.locationIds,
      customerIds: uiFilters.customerIds,
      customerLocations: uiFilters.customerLocations,
      functionalStatuses: uiFilters.functionalStatuses,
      locationCompanyTypes: uiFilters.locationCompanyTypes,
      scrapped: uiFilters.scrapped,
    };
    setDefaultFilters(currentFilters);
    menu.close();
  };

  /**
   * Clears all filters except inventoryStatus and transitStatus (controlled by toggle buttons).
   * Also clears the search field.
   */
  const handleClearAllFilters = () => {
    // Clear search field immediately
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }

    // Preserve inventoryStatus and transitStatus when clearing filters
    // (these are controlled by the toggle buttons)
    const clearedSearch: Record<string, unknown> = {};
    if (searchParameters.inventoryStatus) {
      clearedSearch.inventoryStatus = searchParameters.inventoryStatus;
    }
    if (searchParameters.transitStatus) {
      clearedSearch.transitStatus = searchParameters.transitStatus;
    }

    navigate({ search: clearedSearch });
  };

  // Calculate if clear button should be shown based on active filters.
  // Note: inventoryStatus and transitStatus are excluded as they're controlled by toggle buttons.
  const showClearButton =
    uiFilters.functionalStatuses.length > 0 ||
    uiFilters.productIds.length > 0 ||
    uiFilters.locationIds.length > 0 ||
    uiFilters.customerLocations.length > 0 ||
    uiFilters.locationCompanyTypes.length > 0 ||
    uiFilters.ownership.length > 0 ||
    uiFilters.customerIds.length > 0 ||
    uiFilters.assignment.length > 0 ||
    uiFilters.scrapped ||
    // Deep-linked and invisible, so the clear button is the only way out of it.
    searchParameters.currency !== undefined ||
    (searchParameters.query !== undefined &&
      searchParameters.query.trim() !== '');

  const handleOpenMultiMoveDialog = () => {
    multiMoveDialog.open();
    navigate({ search: { ...searchParameters, move: true } });
  };

  return (
    <Stack>
      <AssetImportModal
        isOpen={assetImportModal.isOpen}
        onClose={assetImportModal.close}
      />
      <AssetMultiMoveDialog
        onClose={() => {
          multiMoveDialog.close();
          if (searchParameters.move) {
            navigate({ search: { ...searchParameters, move: undefined } });
          }
        }}
        isOpen={multiMoveDialog.isOpen}
      />
      <PageContent>
        {/* Action bar with inventory toggle on left, buttons on right */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <FilterToggleGroup
            value={currentLocationFilter}
            options={ASSET_LOCATION_FILTER_OPTIONS}
            onChange={handleLocationToggleChange}
            ariaLabel="asset location filter"
          />
          <Stack direction="row" spacing={1}>
            <Button
              onClick={assetImportModal.open}
              startIcon={<AddRounded />}
              variant="contained"
            >
              Add asset data
            </Button>
            <Button
              onClick={handleOpenMultiMoveDialog}
              startIcon={<ForkRightOutlined />}
              variant={'contained'}
            >
              Move assets
            </Button>
          </Stack>
        </Stack>
        <TableControlContainer>
          <Stack
            useFlexGap
            spacing={1}
            flexWrap="wrap"
            direction="row"
            marginRight="auto"
          >
            <AssetFunctionalStatusFilterMenu
              value={uiFilters.functionalStatuses}
              options={functionalStatusOptions}
              onChange={handleChangeFunctionalStatuses}
            />
            <ItemFilterMenu
              label="Product"
              emptyLabel="No products found"
              value={uiFilters.productIds}
              type="DEVICE"
              includeArchived={true}
              onChange={handleChangeProducts}
            />
            <AssetLocationFilterMenu
              locationIds={uiFilters.locationIds}
              customerIds={uiFilters.customerLocations}
              companyTypes={uiFilters.locationCompanyTypes}
              onChange={handleChangeLocations}
            />
            <AssetOwnershipFilterMenu
              values={uiFilters.ownership}
              customerIds={uiFilters.customerIds}
              onChange={handleChangeOwnership}
            />
            <AssetAssignmentFilterMenu
              value={uiFilters.assignment}
              onChange={handleChangeAssignment}
            />
            {showClearButton && (
              <Button
                onClick={handleClearAllFilters}
                variant="text"
                color="primary"
                size="small"
                sx={{ marginLeft: 2 }}
              >
                Clear filters
              </Button>
            )}
          </Stack>
          <TextField
            inputRef={searchContainer}
            size="small"
            label="Search assets"
            defaultValue={searchParameters.query}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={handleChangeQuery}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
            InputProps={{
              endAdornment: searchParameters.query && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (searchContainer.current) {
                        searchContainer.current.value = '';
                      }
                      updateQuery('');
                    }}
                  >
                    <ClearRounded fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton ref={menuButtonRef} onClick={menu.open}>
            <Badge variant="dot" color="error" invisible={!uiFilters.scrapped}>
              <SettingsRounded fontSize="small" />
            </Badge>
          </IconButton>
          <Popover
            anchorEl={menuButtonRef.current}
            open={menu.isOpen}
            onClose={menu.close}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <List dense>
              <ListItemButton onClick={handleChangeScrapped}>
                <ListItemText>
                  <Typography variant="body2">
                    Include scrapped assets
                  </Typography>
                </ListItemText>
                <Switch
                  edge="end"
                  size="small"
                  onChange={handleChangeScrapped}
                  checked={uiFilters.scrapped}
                />
              </ListItemButton>
              <ListItemButton onClick={handleSaveDefaultFilters}>
                <ListItemIcon>
                  <SaveOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">
                    Save current filters as default
                  </Typography>
                </ListItemText>
              </ListItemButton>
            </List>
          </Popover>
        </TableControlContainer>
        <SupportErrorBoundary message="An error occurred.">
          <Suspense fallback={<AssetTableSkeleton columns={6} />}>
            <AssetTable
              showDownloadIndicator
              sort={sort}
              filter={assetFilter}
              onSort={handleSort}
              onPageChange={handleChangePage}
            />
          </Suspense>
        </SupportErrorBoundary>
      </PageContent>
    </Stack>
  );
}
